/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
	type Ref,
} from 'react';
import { isProjectsInJiraM2Enabled } from '@jira/platform__services-native-integration-projects/src/index.tsx';
import {
	ExpandableMenuItem,
	type ExpandableMenuItemProps,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import type { MenuId } from '@jira/navigation-apps__sidebar-nav4-context/src/common/types.tsx';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useSidebarNav4Controller } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-controller-context/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';

type Props = Omit<ExpandableMenuItemProps, 'isDefaultExpanded'> & {
	// Used for setting the ref specifically for the expanded or collapsed state
	// For example - triggering entrypoint load/unload on hovering over the collapsed menu item
	expandedRef?: Ref<HTMLDivElement>;
	collapsedRef?: Ref<HTMLDivElement>;
	// dropIndictor used to be type: false | JSXElement | null. ReactNode is all of these.
	dropIndicator?: ReactNode;
	dropTargetRef?: React.MutableRefObject<HTMLDivElement | null>;
	isDragging?: boolean;
	menuId: MenuId;
	onSystemToggle?: (isExpanded: boolean) => void;
};

/**
 * Nav4ExpandableMenuItem provides `menuId` instead of `isDefaultExpanded`
 * `menuId` is used to set the expanded-state of the underlying ExpandableMenuItem, using the useSidebarNav4() hook.
 *
 * Ideally, we want to pass the menuId to the trigger element.
 */
export const Nav4ExpandableMenuItem: React.MemoExoticComponent<
	React.ForwardRefExoticComponent<
		Omit<ExpandableMenuItemProps, 'isDefaultExpanded'> & {
			// Used for setting the ref specifically for the expanded or collapsed state
			// For example - triggering entrypoint load/unload on hovering over the collapsed menu item
			expandedRef?: Ref<HTMLDivElement>;
			collapsedRef?: Ref<HTMLDivElement>;
			// dropIndictor used to be type: false | JSXElement | null. ReactNode is all of these.
			dropIndicator?: ReactNode;
			dropTargetRef?: React.MutableRefObject<HTMLDivElement | null>;
			isDragging?: boolean;
			menuId: MenuId;
			onSystemToggle?: (isExpanded: boolean) => void;
		} & React.RefAttributes<HTMLDivElement>
	>
> = memo(forwardRef<HTMLDivElement, Props>(Nav4ExpandableMenuItemView));

function Nav4ExpandableMenuItemView(props: Props, ref: Ref<HTMLDivElement>) {
	const { isSelectedPath, isInitialSelectedPath } = useSidebarNav4();
	const { isControlledNav, isControlledPath } = useSidebarNav4Controller();
	const {
		children,
		isExpanded = false,
		expandedRef,
		collapsedRef,
		dropIndicator = null,
		dropTargetRef = null,
		isDragging = false,
		menuId,
		onExpansionToggle,
		onSystemToggle,
	} = props;
	// controlled state, `isActuallyExpanded` decides whether the component is expanded or not (based on logic in this component and props.isExpanded)
	const [isActuallyExpanded, setActuallyExpanded] = useState<boolean>(
		isInitialSelectedPath(menuId) || isExpanded,
	);

	const userCollapsedRef = useRef(false);

	// By default, the expandable component is closed. When we want it to be initially open, we also need to trigger onExpansionToggle().
	useEffect(() => {
		if (isActuallyExpanded === true && isExpanded === false) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
			onSystemToggle?.(isActuallyExpanded);
		}
		// We want to run this only once, on init.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// If isSelectedPath changes (external change), update our internal state.
	useEffect(() => {
		// Skip auto-expand if user just manually collapsed this menu
		if (userCollapsedRef.current && isProjectsInJiraM2Enabled()) {
			userCollapsedRef.current = false;
			return;
		}

		// Fyi, the following logic causes the component to _collapse_ when the selectedPath changes (preserved here for posterity):
		//   const localIsExpanded = isSelectedPath(menuId);

		// ...but this logic _keeps the component expanded_ (if it is) when the selectedPath changes. We can only auto-expand component, not auto-collapse it though (which is fine, for current UX):
		const localIsExpanded = isSelectedPath(menuId) || isActuallyExpanded;

		// Trigger the callbacks ONLY when the previous state doesn't match the new state.
		if (localIsExpanded !== isActuallyExpanded) {
			setActuallyExpanded(localIsExpanded);
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onSystemToggle?.(localIsExpanded);
		}

		// We only want to run this when isSelectedPath() changes, not when isActuallyExpanded changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSelectedPath]);

	// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5

	useEffect(() => {
		if (!isControlledNav) {
			return;
		}

		// Skip auto-expand if user just manually collapsed this menu
		if (userCollapsedRef.current && isProjectsInJiraM2Enabled()) {
			userCollapsedRef.current = false;
			return;
		}

		const localIsExpanded = isControlledPath(menuId);
		if (localIsExpanded !== isActuallyExpanded) {
			setActuallyExpanded(localIsExpanded);
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			onSystemToggle?.(localIsExpanded);
		}
		// We only want to run this when isControlledPath() changes, not when isActuallyExpanded changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isControlledPath]);

	const expandableMenuItemRef = useMemo(() => {
		if (isActuallyExpanded) {
			return expandedRef ?? ref;
		}
		return collapsedRef ?? ref;
	}, [collapsedRef, expandedRef, isActuallyExpanded, ref]);

	const onExpandableMenuItemToggle = useCallback(
		(hasExpanded: boolean) => {
			setActuallyExpanded(hasExpanded);
			onExpansionToggle?.(hasExpanded);

			// Track manual collapse to prevent auto-expand flicker on subsequent path changes
			// When user deliberately collapses, we skip one cycle of auto-expand
			if (!hasExpanded && isProjectsInJiraM2Enabled()) {
				userCollapsedRef.current = true;
			}
		},
		[onExpansionToggle],
	);

	return (
		<ExpandableMenuItem
			dropIndicator={dropIndicator}
			isExpanded={isActuallyExpanded && !isDragging}
			onExpansionToggle={onExpandableMenuItemToggle}
			ref={mergeRefs(expandableMenuItemRef, dropTargetRef)}
		>
			{children}
		</ExpandableMenuItem>
	);
}
